import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays } from "lucide-react";
import { toast } from "sonner";
import SectionCard from "@/components/common/SectionCard";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { getGoogleCalendar, connectGoogleCalendar, disconnectGoogleCalendar, syncGoogleCalendar } from "@/services/google-calendar.service";
import { calendarMessage } from "@/utils/google-calendar.utils";
import { formatDateTime } from "@/utils/common.utils";

const KEY = ["google-calendar"] as const;

export default function GoogleCalendarSettings() {
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const query = useQuery({ queryKey: KEY, queryFn: getGoogleCalendar, refetchInterval: 15000 });
  const connection = query.data;
  const connect = useMutation({
    mutationFn: connectGoogleCalendar,
    onSuccess: url => { window.location.assign(url); },
    onError: (error: Error) => toast.error(error.message),
  });
  const sync = useMutation({
    mutationFn: syncGoogleCalendar,
    onSuccess: data => {
      queryClient.setQueryData(KEY, data);
      if (data.last_error) toast.error(calendarMessage(data));
      else toast.success(data.syncing ? "Calendar sync is already running" : "Calendar synced");
    },
    onError: (error: Error) => toast.error(error.message),
  });
  const disconnect = useMutation({
    mutationFn: disconnectGoogleCalendar,
    onSuccess: data => {
      queryClient.setQueryData(KEY, data);
      setConfirmDisconnect(false);
      toast.success("Google Calendar disconnected");
    },
    onError: (error: Error) => toast.error(error.message),
  });
  const outcome = params.get("google");
  useEffect(() => {
    if (!outcome) return;
    if (outcome === "connected") toast.success("Google Calendar connected");
    else if (outcome === "denied") toast.info("Google connection cancelled");
    else toast.error("Google could not connect. Try again and allow the calendar permissions.");
    const next = new URLSearchParams(params);
    next.delete("google");
    setParams(next, { replace: true });
    queryClient.invalidateQueries({ queryKey: KEY });
  }, [outcome, params, setParams, queryClient]);
  const busy = connect.isPending || sync.isPending || disconnect.isPending || connection?.syncing;

  return (
    <SectionCard title="Google Calendar" description="Your main calendar, connected to your Stoop deals." data-testid="google-calendar-settings">
      {query.isLoading ? (
        <p role="status" className="text-sm text-dash-muted">Loading your calendar connection…</p>
      ) : query.isError || !connection ? (
        <div className="space-y-3">
          <p role="alert" className="text-sm text-dash-muted">Couldn't load your calendar connection.</p>
          <Button variant="dashboardOutline" onClick={() => query.refetch()}>Retry</Button>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex items-start gap-3 rounded-lg bg-dash-hover p-4">
            <CalendarDays className="mt-0.5 size-5 shrink-0 text-dash-muted" aria-hidden="true" />
            <div className="min-w-0 space-y-1">
              <p className="break-words text-sm font-medium text-dash-ink">
                {connection.status !== "disconnected" ? connection.email : "No calendar connected"}
              </p>
              <p role="status" className="text-sm text-dash-muted">{calendarMessage(connection)}</p>
              {connection.last_synced_at && connection.status !== "disconnected" && (
                <p className="text-xs text-dash-faint">Last synced {formatDateTime(connection.last_synced_at)}</p>
              )}
            </div>
          </div>
          <p className="text-sm leading-relaxed text-dash-muted">
            Showings, meetings, and key dates for deals assigned to you appear automatically.
            Moving or cancelling a Stoop event in Google updates Stoop too. Stoop sends
            attendee invitations so nobody gets duplicate emails from Google.
          </p>
          <div className="flex flex-wrap gap-3">
            {connection.configured && connection.status !== "connected" && (
              <Button variant="dashboardPrimary" disabled={busy} loading={connect.isPending} onClick={() => connect.mutate()}>
                {connection.status === "reconnect_required" ? "Reconnect Google" : "Connect Google Calendar"}
              </Button>
            )}
            {connection.configured && connection.status === "connected" && (
              <Button variant="dashboardPrimary" disabled={busy} loading={sync.isPending} onClick={() => sync.mutate()}>Sync now</Button>
            )}
            {connection.status !== "disconnected" && (
              <Button variant="dashboardOutline" disabled={connect.isPending || disconnect.isPending} onClick={() => setConfirmDisconnect(true)}>Disconnect</Button>
            )}
          </div>
          <p className="text-xs text-dash-faint">Your other Google events stay outside Stoop.</p>
        </div>
      )}
      <ConfirmDialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}
        title="Disconnect Google Calendar?" description="Automatic sync will stop. Existing events stay on Google Calendar, and your appointments stay in Stoop."
        confirmLabel="Disconnect" onConfirm={async () => { try { await disconnect.mutateAsync(); } catch { /* mutation displays the error */ } }} />
    </SectionCard>
  );
}
